//import THREE from "three";
import Gate, { LAYER_NONE } from "./gate";
import RenderManager from "../rendering/render-manager";
import PhysicalCameraControls from "../camera/physical-camera";
import { calcAnimated } from "../slides/animation/tweenAnimation";
import { meshRaycaster } from "../interactivity/add-interactivity";
import { fire } from "../utils/events";
import ensureArray from '../utils/ensure-array';

const g_calcOutsideWorld_scale = new THREE.Vector3();
const g_calcOutsideWorld_position = new THREE.Vector3();
const g_calcOutsideWorld_quaternion = new THREE.Quaternion();
const g_calcOutsideWorld_matrix = new THREE.Matrix4();
const g_calcInsideWorld_scale = new THREE.Vector3();
const g_calcInsideWorld_quaternion = new THREE.Quaternion();
const g_calcInsideWorld_matrix = new THREE.Matrix4();
const g_calcInsideWorld_matrixInverse = new THREE.Matrix4();
const g_exit_raycaster = new THREE.Vector2();
const g_exit_position = new THREE.Vector3();
const g_exit_matrixCamera = new THREE.Matrix4();
const g_teleport_matrixCamera = new THREE.Matrix4();
const g_teleport_position = new THREE.Vector3();
const g_teleport_raycaster = new THREE.Vector2();
const g_updateTransfer_cameraPosition = new THREE.Vector3();
const g_updateTransfer_position = new THREE.Vector3();
const g_updateTransfer_scale = new THREE.Vector3();
const g_updateTransfer_radius = new THREE.Vector3();
const g_calcTweenOutside_positionCamera = new THREE.Vector3();
const g_calcTweenOutside_normal = new THREE.Vector3();
const g_calcTweenOutside_radius = new THREE.Vector3();
const g_calcTweenOutside_point = new THREE.Vector3();
const g_calcTweenOutside_target = new THREE.Vector3();
const g_calcTweenInside_positionCamera = new THREE.Vector3();
const g_calcTweenInside_normal = new THREE.Vector3();
const g__calcTweenIntoPosition_quaternion = new THREE.Quaternion();
const g__calcTweenIntoPosition_scale = new THREE.Vector3();
const g__calcTweenIntoPosition__normal = new THREE.Vector3();
const g__calcTweenIntoPosition__point = new THREE.Vector3();
const g__calcTweenIntoPosition_target = new THREE.Vector3();

const Network = new class Network {
    constructor() {
        this._activeWorld = null;
        /**
         * @type {World[]}
         */
        this._worlds = {};
        this._transferEnabled = true;
        this._mainWorld = null;
    }

    get transferEnabled() { return this._transferEnabled; }
    set transferEnabled(value) { this._transferEnabled = value; }

    get mainWorld() { return this._mainWorld; }
    set mainWorld(val) { this._mainWorld = val; }

    /**
     * 
     * @param {THREE.Scene} world 
     * @param {Gate} gate 
     */
    regiterGate(world, gate) {
        const _world = this._worlds[world.uuid];

        if (!_world) return;

        _world.gates.push(gate);

        this.registerWorld(gate.scene, world, gate);
    }

    /**
     * 
     * @param {THREE.Scene} world 
     */
    registerWorld(world, parent = null, gate = null) {
        if (this._worlds[world.uuid]) return;

        this._worlds[world.uuid] = new World({
            parent: parent,
            gates: [],
            gate: gate,
            scene: world
        });
    }

    /**
     * 
     * @param {THREE.Scene} world 
     */
    setActive(world) { this._activeWorld = world; }

    get gates() { return !this._activeWorld ? [] : this._worlds[this._activeWorld.uuid].gates; }
    get parent() { return !this._activeWorld ? null : this._worlds[this._activeWorld.uuid].parent; }
    get world() { return !this._activeWorld ? null : this._worlds[this._activeWorld.uuid]; }
    get currentWorldUuid() { return this.parent ? this.currentWorld.uuid : null; }
    get currentWorld() { return this._activeWorld; }

    get parentWorldUuid() {
        const world = this.parent;

        return world ? world.uuid : null;
    }

    /**
     * 
     * @param {string} uuid 
     * @returns {World?}
     */
    getWorld(uuid) {
        uuid = uuid || this._mainWorld.uuid;

        return this._worlds[uuid] || null;
    }

    getParentWorld(gate) { return gate.parent ? this._worlds[gate.parent.uuid] : null; }

    depthFirstSearch(world1, world2) {

        const start = world1.scene.uuid;
        const goal = world2.scene.uuid;

        const worlds = this._worlds;

        const stack = [{ vertex: start, path: [] }];
        const visited = {};

        function getNeighbors(vertex) {
            const neighbors = [];
            const world = worlds[vertex];

            if (world.parent) neighbors.push({ uuid: world.parent.uuid, out: true });

            world.gates.forEach(x => neighbors.push({ uuid: x.scene.uuid, out: false }));

            return neighbors;
        }

        while (stack.length > 0) {
            const { vertex, path } = stack.pop();

            if (!visited[vertex]) {
                if (vertex === goal) return path;

                visited[vertex] = true;

                getNeighbors(vertex).forEach(x => stack.push({
                    vertex: x.uuid,
                    path: path.concat([{ out: x.out, uuid: x.uuid }])
                }));
            }
        }

        return null;
    }

    calcOutsideWorld({ sOrigin, sScale, sRadius }, ...points) {
        const invScale = 1 / sRadius / sScale;
        const scale = g_calcOutsideWorld_scale.set(invScale, invScale, invScale);
        const quaternion = g_calcOutsideWorld_quaternion.set(0, 0, 0, 1);
        const position = g_calcOutsideWorld_position.set(0, 0, 0);

        const matrix = g_calcOutsideWorld_matrix.compose(position, quaternion, scale);

        const result = points.map(x => {
            const p = new THREE.Vector3().copy(x)
                .applyMatrix4(matrix)
                .multiplyScalar(sScale)
                .add(sOrigin);

            return p;
        });

        const count = result.length;
        if (count === 0) return;
        if (count === 1) return result[0];
        return result;
    }

    calcInsideWorld({ sOrigin, sScale, sRadius }, ...points) {
        const sInvScale = 1 / sRadius / sScale;
        const scale = g_calcInsideWorld_scale.set(sInvScale, sInvScale, sInvScale);
        const quaternion = g_calcInsideWorld_quaternion.set(0, 0, 0, 1);

        const matrix = g_calcInsideWorld_matrix.compose(sOrigin, quaternion, scale);
        const matrixInverse = g_calcInsideWorld_matrixInverse.getInverse(matrix);

        const result = points.map(x => {
            return new THREE.Vector3().copy(x).applyMatrix4(matrixInverse);
        });

        const count = result.length;

        if (count === 0) return;
        if (count === 1) return result[0];

        return result;
    }

    exit(camera, controls, world) {
        const gate = world.gate;
        const matrixCamera = g_exit_matrixCamera.copy(camera.matrixWorld);
        const position = g_exit_position.setFromMatrixPosition(matrixCamera);

        const [oPosition, oTarget] = this.calcOutsideWorld({ sOrigin: gate.position, sScale: 1, sRadius: gate.boundingRadius }, position, controls.target);

        camera.position.copy(oPosition);
        controls.target.copy(oTarget);
        camera.lookAt(controls.target);
        controls.resetDistance();
        controls.update();

        gate.root.scale.set(1, 1, 1);
        gate.root.position.set(0, 0, 0);
        RenderManager.setActiveScene(world.parent);

        fire("unselect");

        camera.far = this._worlds[this._activeWorld.uuid].far;
        camera.updateProjectionMatrix();

        camera.updateMatrixWorld();
        meshRaycaster.setFromCamera(g_exit_raycaster, camera);
        camera.update = true;
    }

    teleport(camera, controls, gate) {
        const matrixCamera = g_teleport_matrixCamera.copy(camera.matrixWorld);
        const position = g_teleport_position.setFromMatrixPosition(matrixCamera);

        const [oPosition, oTarget] = this.calcInsideWorld({
            sOrigin: gate.position,
            sScale: 1,
            sRadius: gate.boundingRadius
        }, position, controls.target);

        camera.position.copy(oPosition);
        controls.target.copy(oTarget);
        camera.lookAt(controls.target);
        controls.update();

        this._worlds[this._activeWorld.uuid].far = camera.far;

        gate.root.scale.set(1, 1, 1);
        gate.root.position.set(0, 0, 0);
        RenderManager.setActiveScene(gate.scene);

        fire("unselect")

        camera.far = Q3.data.setups.camera.far * gate.boundingRadius * 10;
        camera.updateProjectionMatrix();

        camera.updateMatrixWorld();
        meshRaycaster.setFromCamera(g_teleport_raycaster, camera);
        camera.update = true;
    }

    setWorld(world) {
        RenderManager.setActiveScene(world.scene);

        world.scene.scale.set(1, 1, 1);
        world.scene.position.set(0, 0, 0);
    }

    /**
     * 
     * @param {THREE.Camera} camera 
     * @param {PhysicalCameraControls} controls
     */
    updateTransfer(sceneBoundingBox, camera = RenderManager.activeCamera, controls = Q3.controls, smallBoundry = false) {
        if (!this._transferEnabled) return;

        const gates = this.gates;
        const cameraPosition = g_updateTransfer_cameraPosition.setFromMatrixPosition(camera.matrixWorld);

        const world = this.world;
        if (world.parent) {
            world.gate.update(sceneBoundingBox);

            const radius = world.gate.boundingRadius * (smallBoundry ? 1 : 2.5) * world.gate.getWorldScale(g_updateTransfer_radius).x;

            if (cameraPosition.lengthSq() > radius * radius) {
                this.exit(camera, controls, world);
                return;
            }
        }

        for (let i = 0, len = gates.length; i < len; i++) {
            /**
             * @type {Gate}
             */
            const gate = gates[i];
            if (gate.visible) {
                const position = g_updateTransfer_position.setFromMatrixPosition(gate.matrixWorld);
                const scale = g_updateTransfer_scale.setFromMatrixScale(gate.matrixWorld);

                if (cameraPosition.distanceToSquared(position) < scale.lengthSq() * 0.26666) {
                    this.teleport(camera, controls, gate, false);
                    break;
                }
            }
        }
    }

    insideCurrentWorld(object) {
        if ("overridePicker" in object) {
            object = object.overridePicker;
        } else if (object.isSplineControlPoint) {
            object = object.spline;
        }

        const asset = Q3.data.assets.objects[object.uuid];

        if (!asset) return false;

        const objectWorld = asset.parent_world || null;

        return objectWorld === this.currentWorldUuid;
    }

    createGate({ worldUuid = null, parentUuid = null } = {}) {
        if (parentUuid && !this._worlds[parentUuid]) return;

        const gate = new Gate();
        let world = this._activeWorld;

        if (Q3.editMode) gate.addHelpers(0xff0000);

        if (worldUuid) { gate.scene.uuid = worldUuid; }
        if (parentUuid) { world = this._worlds[parentUuid].gate.scene; }

        this.regiterGate(world, gate);

        return gate;
    }

    /**
     * 
     * @param {THREE.WebGLRenderer} renderer 
     * @param {THREE.PerspectiveCamera} camera
     * @param {number} layer
     */
    renderParent(renderer, camera, layer, target) {
        const world = this.world;
        const scene = this.parent;

        if (!scene) return;

        camera.layers.set(LAYER_NONE);

        const scale = world.gate.boundingRadius;
        scene.scale.set(scale, scale, scale);
        world.gate.getWorldPosition(scene.position).multiplyScalar(-scale);

        scene.traverse(object => {
            if (object.material) {
                let materials = ensureArray(object.material);
                for (let material of materials) {
                    if (material.isMeshNestedMaterial) {
                        material.updateLayers(object);
                    }
                }
            }
        });

        renderer.render(scene, camera, target);

        scene.scale.set(1, 1, 1);
        scene.position.set(0, 0, 0);
        scene.updateMatrixWorld();
    }

    calcTweenOutside(gate, camera) {
        const positionCamera = g_calcTweenOutside_positionCamera.setFromMatrixPosition(camera.matrixWorld);
        const normal = g_calcTweenOutside_normal.copy(positionCamera).normalize();
        const radius = gate.boundingRadius * 3 * gate.getWorldScale(g_calcTweenOutside_radius.set(0, 0, 0)).x;
        const point = g_calcTweenOutside_point.copy(normal).multiplyScalar(radius);
        const target = g_calcTweenOutside_target.set(0, 0, 0);
        const outside = this.calcExitedPoints(gate, point, target);

        return { position: point, target, other: { position: outside[0], target: outside[1] } };
    }

    calcTweenInside(gate, camera, slideId) {
        const positionCamera = g_calcTweenInside_positionCamera.setFromMatrixPosition(camera.matrixWorld);
        const { position: positionGate } = calcAnimated(gate, slideId);
        const normal = g_calcTweenInside_normal.subVectors(positionCamera, positionGate).normalize();
        const point = positionGate;
        const { position: _point, target } = this._calcTweenIntoPosition(gate, point, normal);
        const inside = this.calcTeleportedPoints(gate, _point, target)

        return { position: _point, target, other: { position: inside[0], target: inside[1] } };
    }

    _calcTweenIntoPosition(gate, point, normal) {
        const quaternion = g__calcTweenIntoPosition_quaternion.setFromRotationMatrix(gate.matrixWorld);
        const scale = (g__calcTweenIntoPosition_scale.setFromMatrixScale(gate.matrixWorld).x * .77);
        const _normal = g__calcTweenIntoPosition__normal.copy(normal).applyQuaternion(quaternion);
        const _point = g__calcTweenIntoPosition__point.copy(_normal).multiplyScalar(scale).add(point);
        const target = g__calcTweenIntoPosition_target.copy(point);

        return { position: _point, target };
    }

    zoomInto(gate, point, normal) {
        const { position: _point, target } = this._calcTweenIntoPosition(gate, point, normal);

        const tween = {
            camX: RenderManager.activeCamera.position.x,
            camY: RenderManager.activeCamera.position.y,
            camZ: RenderManager.activeCamera.position.z,
            tarX: Q3.controls.target.x,
            tarY: Q3.controls.target.y,
            tarZ: Q3.controls.target.z
        };

        RenderManager.activeCamera.activeTween = TweenLite.to(
            tween,
            Math.min(_point.distanceTo(RenderManager.activeCamera.position), 3),
            {
                camX: _point.x,
                camY: _point.y,
                camZ: _point.z,
                tarX: target.x,
                tarY: target.y,
                tarZ: target.z,
                onUpdate() {
                    Q3.controls.target.set(tween.tarX, tween.tarY, tween.tarZ);
                    RenderManager.activeCamera.position.set(tween.camX, tween.camY, tween.camZ);
                    RenderManager.activeCamera.lookAt(Q3.controls.target);
                    Q3.controls.update();
                    RenderManager.activeCamera.update = true;
                    Network.transferEnabled = false;
                },
                onComplete() {
                    Network.transferEnabled = true;
                    RenderManager.activeCamera.update = true;
                    Q3.controls.update();
                }
            }
        );
    }

    getObjectByProperty(name, value) {
        const root = RenderManager.root;

        let object = root.getObjectByProperty(name, value);

        if (object) return object;

        Object.keys(this._worlds)
            .find(key => {
                object = this._worlds[key].scene.getObjectByProperty(name, value);

                return !!object;
            });

        return object;
    }
}

export default Network;
export { Network };

class World {
    constructor({ parent, gates, gate, scene }) {
        /**
         * @type {World?}
         */
        this.parent = parent;
        /**
         * @type {Gate[]}
         */
        this.gates = gates;
        /**
         * @type {Gate}
         */
        this.gate = gate;
        /**
         * @type {THREE.Scene}
         */
        this.scene = scene;
        this.far = Q3.data.setups.camera.far;
    }
}